// LDWrapper.jsx
import React, { useEffect, useState } from "react";
import { isAuthenticated } from "../services/login";
import { LDProvider } from "launchdarkly-react-client-sdk";

const fallbackContext = {
  kind: "multi",
  user: {
    key: "user-key-123abc",
    name: "Sandy Smith",
    email: "sandy@example.com",
  },
  //store_id added
  store_id: {
    key: "unknown-store",
  },
};

// Feature flags are an ENHANCEMENT: they must never decide whether the app
// renders at all. This wrapper used to block on `if (!ldContext) return <div/>`
// until isAuthenticated() resolved, so anything that stopped it resolving - a
// hung auth call, or the 401 interceptor's old never-settling promise - left the
// ENTIRE app as a blank white page with no error and no telemetry. Past this
// deadline we render with the fallback context: flags evaluate to their
// defaults, which is strictly better than showing nothing.
const LD_CONTEXT_TIMEOUT_MS = 3000;

export const LDWrapper = ({ children }) => {
  const [ldContext, setLdContext] = useState(null);

  useEffect(() => {
    let settled = false;
    const apply = context => {
      if (settled) return;
      settled = true;
      setLdContext(context);
    };

    // Deadline runs independently of the request, so it fires even if the
    // promise below never settles at all.
    const deadline = setTimeout(() => apply(fallbackContext), LD_CONTEXT_TIMEOUT_MS);

    const fetchLDContext = async () => {
      try {
        const { success, user, store, role } = await isAuthenticated();
        if (success && user) {
          const context = {
            kind: "multi",
            user: {
              key: user?.email,
              name: `${user?.first_name}${user?.last_name ? ` ${user?.last_name}` : ""}`,
              email: user?.email,
              custom: {
                role,
                user_id: user?.id,
                store_numeric_id: store?.store_id,
                store_name: store?.store_allias_name || store?.name,
                client_id: store?.client?.id,
                client_name: store?.client?.name,
                account_detail_id: store?.client?.account_detail_id,
              },
            },
            store_id: {
              key: store?.id,
            },
          };
          apply(context);
        } else {
          apply(fallbackContext);
        }
      } catch (err) {
        console.error("LDWrapper: Error fetching context", err);
        apply(fallbackContext);
      }
    };

    fetchLDContext();

    return () => clearTimeout(deadline);
  }, []);

  if (!ldContext) return <div></div>;

  return (
    <LDProvider clientSideID={process.env.REACT_APP_LDCLIENT_ID} context={ldContext} options={{ streaming: true }}>
      {children}
    </LDProvider>
  );
};
